/**
 * Page Management Module (Refactored)
 * Facade that coordinates navigation and page controllers
 *
 * Following OOA/OOD principles:
 * - Facade Pattern: Single entry point for page management
 * - Strategy Pattern: Page controllers implement common interface
 * - Dependency Injection: Services passed to controllers
 *
 * Original: 2,416 lines
 * Refactored: ~200 lines (90% reduction)
 */

import { PageName, PageConfig, PageController } from './types/page-types';
import { NavigationManager } from './navigation/navigation-manager';
import { PaginationController } from './pagination/pagination-controller';
import { ControllerServices } from './controllers/base-controller';
import { DashboardSelectorController } from './controllers/dashboard-selector-controller';
import { DiscoveryController } from './controllers/discovery-controller';
import { DevicesController } from './controllers/devices-controller';
import { ApplicationsController } from './controllers/applications-controller';
import { RelationshipsController } from './controllers/relationships-controller';
import { SettingsController } from './controllers/settings-controller';
import { KnowledgeBaseController } from './controllers/knowledge-base-controller';
import { BusinessCapabilitiesController } from './controllers/business-capabilities-controller';
import { BankTopologyController } from './controllers/bank-topology-controller';
import { ServiceMapController } from './controllers/service-map-controller';
import { EAController } from './controllers/ea-controller';
import { UserAdminController } from './controllers/user-admin-controller';
import { safeFeatherReplace } from '../utils/feather-helper';
import { ActivityLogManager, createActivityLogManager } from './managers/activity-log-manager';

/**
 * PageManager class - Refactored
 * Coordinates page navigation and initialization via specialized controllers
 */
export class PageManager {
    private navigationManager: NavigationManager;
    private paginationController: PaginationController;
    private controllers: Map<PageName, PageController>;
    private services: ControllerServices;
    private settingsService: any;
    private canisterClient: any;

    constructor() {
        // Initialize services
        this.settingsService = new (window as any).SettingsService();
        this.canisterClient = (window as any).canisterClient;

        this.services = {
            canisterClient: this.canisterClient,
            settingsService: this.settingsService
        };

        // Initialize navigation manager
        this.navigationManager = new NavigationManager();

        // Initialize pagination controller
        this.paginationController = new PaginationController(this.settingsService);

        // Initialize page controllers
        this.controllers = new Map<PageName, PageController>();
        this.initializeControllers();

        // Register pages with navigation
        this.registerPages();

        // Setup navigation
        this.navigationManager.setupNavigation();
        this.navigationManager.setPageChangeCallback(this.handlePageChange.bind(this));

        // Setup pagination with table renderers
        this.setupPagination();
    }

    /**
     * Initialize all page controllers
     */
    private initializeControllers(): void {
        this.controllers.set('dashboard', new DashboardSelectorController(this.services));
        this.controllers.set('discovery', new DiscoveryController(this.services));
        this.controllers.set('devices', new DevicesController(this.services));
        this.controllers.set('applications', new ApplicationsController(this.services));
        this.controllers.set('relationships', new RelationshipsController(this.services));
        this.controllers.set('settings', new SettingsController(this.services));
        this.controllers.set('knowledge', new KnowledgeBaseController(this.services));

        // Business Capabilities controller - expose globally for onclick handlers
        const businessCapabilitiesController = new BusinessCapabilitiesController(this.services);
        this.controllers.set('business-capabilities', businessCapabilitiesController);
        (window as any).businessCapabilitiesController = businessCapabilitiesController;

        // Bank Topology controller (legacy — kept for backward compat)
        this.controllers.set('bank-topology', new BankTopologyController(this.services));

        // Service Map controller (Sprint 47 — replaces Bank Topology in nav)
        this.controllers.set('service-map', new ServiceMapController(this.services));

        // Enterprise Architecture controller
        this.controllers.set('enterprise-architecture', new EAController(this.services));

        // User Admin controller (admin-only)
        this.controllers.set('user-admin', new UserAdminController(this.services));
    }

    /**
     * Register pages with navigation manager
     */
    private registerPages(): void {
        const pageConfigs: Map<PageName, PageConfig> = new Map([
            ['dashboard', {
                title: 'Dashboard',
                contentSelector: '#dashboard-content',
                navSelector: '[data-page="dashboard"]',
                initFunction: () => this.initPage('dashboard')
            }],
            ['discovery', {
                title: 'Discovery',
                contentSelector: '#discovery-content',
                navSelector: '[data-page="discovery"]',
                initFunction: () => this.initPage('discovery')
            }],
            ['devices', {
                title: 'Devices',
                contentSelector: '#devices-content',
                navSelector: '[data-page="devices"]',
                initFunction: () => this.initPage('devices')
            }],
            ['applications', {
                title: 'Applications',
                contentSelector: '#applications-content',
                navSelector: '[data-page="applications"]',
                initFunction: () => this.initPage('applications')
            }],
            ['relationships', {
                title: 'Relationships',
                contentSelector: '#relationships-content',
                navSelector: '[data-page="relationships"]',
                initFunction: () => this.initRelationships()
            }],
            ['vector-search', {
                title: 'Vector Search',
                contentSelector: '#vector-search-content',
                navSelector: '[data-page="vector-search"]',
                initFunction: () => this.initVectorSearch()
            }],
            ['credentials', {
                title: 'Credentials',
                contentSelector: '#credentials-content',
                navSelector: '[data-page="credentials"]',
                initFunction: () => this.initCredentials()
            }],
            ['ii-agent', {
                title: 'Ask Ada',
                contentSelector: '#ii-agent-content',
                navSelector: '[data-page="ii-agent"]',
                initFunction: () => this.initIIAgent()
            }],
            ['service-models', {
                title: 'Service Models',
                contentSelector: '#service-models-content',
                navSelector: '[data-page="service-models"]',
                initFunction: () => this.initServiceModels()
            }],
            ['business-capabilities', {
                title: 'Business Capabilities',
                contentSelector: '#business-capabilities-content',
                navSelector: '[data-page="business-capabilities"]',
                initFunction: () => this.initPage('business-capabilities')
            }],
            ['bank-topology', {
                title: 'Bank Topology',
                contentSelector: '#bank-topology-content',
                navSelector: '[data-page="bank-topology"]',
                initFunction: () => this.initPage('bank-topology')
            }],
            ['service-map', {
                title: 'Service Map',
                contentSelector: '#service-map-content',
                navSelector: '[data-page="service-map"]',
                initFunction: () => this.initPage('service-map')
            }],
            ['enterprise-architecture', {
                title: 'Enterprise Architecture',
                contentSelector: '#enterprise-architecture-content',
                navSelector: '[data-page="enterprise-architecture"]',
                initFunction: () => this.initPage('enterprise-architecture')
            }],
            ['knowledge', {
                title: 'Knowledge Base',
                contentSelector: '#knowledge-content',
                navSelector: '[data-page="knowledge"]',
                initFunction: () => this.initPage('knowledge')
            }],
            ['settings', {
                title: 'Settings',
                contentSelector: '#settings-content',
                navSelector: '[data-page="settings"]',
                initFunction: () => this.initPage('settings')
            }],
            ['user-admin', {
                title: 'User Management',
                contentSelector: '#user-admin-content',
                navSelector: '[data-page="user-admin"]',
                initFunction: () => this.initPage('user-admin')
            }]
        ]);

        this.navigationManager.registerPages(pageConfigs);
    }

    /**
     * Setup pagination for tables
     */
    private setupPagination(): void {
        // Register tables with their renderers
        // Renderers are simple stubs - actual implementation in DevicesController etc.
        this.paginationController.registerTable('devices', () => {});
        this.paginationController.registerTable('applications', () => {});
        this.paginationController.registerTable('relationships', () => {});

        this.paginationController.initialize();
    }

    /**
     * Handle page change - delegate to controller or call initFunction
     */
    private handlePageChange(pageName: PageName): void {
        console.log(`📄 handlePageChange called for: ${pageName}`);
        const controller = this.controllers.get(pageName);
        if (controller) {
            console.log(`📄 Found controller for ${pageName}`);
            controller.init().catch(error => {
                console.error(`Failed to initialize ${pageName}:`, error);
            });
        } else {
            console.log(`📄 No controller for ${pageName}, checking page config...`);
            // For pages without controllers, call the initFunction from page config
            const pageConfig = this.navigationManager.getPageConfig(pageName);
            console.log(`📄 pageConfig for ${pageName}:`, pageConfig);
            if (pageConfig && pageConfig.initFunction) {
                console.log(`📄 Calling initFunction for ${pageName}`);
                const result = pageConfig.initFunction();
                // Handle async init functions
                if (result instanceof Promise) {
                    result.catch(error => {
                        console.error(`Failed to initialize ${pageName}:`, error);
                    });
                }
            } else {
                console.log(`📄 No initFunction found for ${pageName}`);
            }
        }
    }

    /**
     * Initialize page via controller
     */
    private async initPage(pageName: PageName): Promise<void> {
        const controller = this.controllers.get(pageName);
        if (controller) {
            await controller.init();
        }
    }

    /**
     * Public method to show a page
     */
    public showPage(pageName: PageName): void {
        this.navigationManager.showPage(pageName);
    }

    /**
     * Get current page
     */
    public getCurrentPage(): PageName {
        return this.navigationManager.getCurrentPage();
    }

    // ==========================================
    // Legacy methods - to be migrated to controllers
    // ==========================================

    private async initRelationships(): Promise<void> {
        console.log('Initializing Relationships page');
        // TODO: Create RelationshipsController
    }

    private initVectorSearch(): void {
        console.log('Initializing Vector Search page');
        const vectorSearch = (window as any).vectorSearch;
        if (vectorSearch) {
            vectorSearch.initializePage();
        } else {
            console.warn('VectorSearch not available on window');
        }
    }

    private async initCredentials(): Promise<void> {
        console.log('Initializing Credentials page');
        // TODO: Create CredentialsController
    }

    private async initIIAgent(): Promise<void> {
        console.log('Initializing II-Agent page');
        try {
            safeFeatherReplace();
            if ((window as any).iiAgentUI) {
                (window as any).iiAgentUI.setupEventListeners();
                await (window as any).iiAgentUI.loadInitialData();
            }
        } catch (error) {
            console.error('Failed to initialize II-Agent page:', error);
        }
    }

    private async initServiceModels(): Promise<void> {
        console.log('Initializing Service Models page');

        // Show loading message immediately so user knows data is being fetched
        const loadingHtml = '<div class="text-center py-8"><i data-feather="loader" class="w-8 h-8 mx-auto animate-spin text-blue-600"></i><p class="mt-2 text-gray-600">Loading service maps from blockchain...</p></div>';
        const topologyContainer = document.getElementById('topology-models-container');
        const serviceModelsContainer = document.getElementById('service-models-container');
        const analysisContainer = document.getElementById('analysis-models-container');
        if (topologyContainer) topologyContainer.innerHTML = loadingHtml;
        if (serviceModelsContainer) serviceModelsContainer.innerHTML = loadingHtml;
        if (analysisContainer) analysisContainer.innerHTML = loadingHtml;
        // Replace feather icons for spinner
        if ((window as any).feather) {
            (window as any).feather.replace();
        }

        // Update stats cards from vector_db
        await this.updateServiceModelsStats();

        // Load and display service maps
        if ((window as any).serviceMapController) {
            await (window as any).serviceMapController.displayActiveServiceModels();
        }
    }

    /**
     * Update the stats cards on the Service Models page
     */
    private async updateServiceModelsStats(): Promise<void> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) return;

            // Get list of service maps
            const result = await canisterClient.callCanister('vector_db', 'list_service_map_names', []);
            const serviceMapNames = result?.data || result || [];

            // Update service count
            const totalServicesEl = document.getElementById('total-services-count');
            if (totalServicesEl) {
                totalServicesEl.textContent = String(serviceMapNames.length);
            }

            // Calculate total dependencies by summing relationships from all service maps
            let totalDependencies = 0;
            for (const name of serviceMapNames) {
                try {
                    const mapResult = await canisterClient.callCanister('vector_db', 'get_service_map', [name]);
                    const serviceMap = mapResult?.data?.[0] || mapResult?.[0] || mapResult?.data || mapResult;
                    if (serviceMap && serviceMap.tiers) {
                        // Count devices as dependencies
                        for (const tier of serviceMap.tiers) {
                            totalDependencies += tier.devices?.length || 0;
                        }
                    }
                } catch (e) {
                    console.warn(`Failed to get service map ${name}:`, e);
                }
            }

            const totalDepsEl = document.getElementById('total-dependencies-count');
            if (totalDepsEl) {
                totalDepsEl.textContent = String(totalDependencies);
            }
        } catch (error) {
            console.error('Failed to update service models stats:', error);
        }
    }

    // ==========================================
    // Public API for backwards compatibility
    // ==========================================

    public async viewKBDocument(docId: string): Promise<void> {
        const controller = this.controllers.get('knowledge') as KnowledgeBaseController;
        if (controller) {
            await controller.viewKBDocument(docId);
        }
    }

    public exportKBDocument(docId: string): void {
        const controller = this.controllers.get('knowledge') as KnowledgeBaseController;
        if (controller) {
            controller.exportKBDocument(docId);
        }
    }

    public async initKnowledge(): Promise<void> {
        await this.initPage('knowledge');
    }

    public resetServiceMapView(): void {
        const cy = (window as any).currentCytoscape;
        if (cy) {
            cy.fit();
            cy.center();
        }
    }

    public exportServiceMap(): void {
        const cy = (window as any).currentCytoscape;
        if (cy) {
            const png = cy.png({ output: 'blob', full: true, scale: 2 });
            const url = URL.createObjectURL(png);
            const a = document.createElement('a');
            a.href = url;
            a.download = `service-map-${Date.now()}.png`;
            a.click();
            URL.revokeObjectURL(url);
        }
    }

    /**
     * View device details in modal
     */
    public async viewDevice(deviceId: string, buttonElement?: HTMLElement): Promise<void> {
        console.log('🔍 Viewing device:', deviceId);

        const toastId = (window as any).showToast?.('Loading device details...', 'info');

        let button: HTMLButtonElement | null = buttonElement as HTMLButtonElement || null;
        if (!button) {
            button = document.querySelector(`button[onclick*="viewDevice('${deviceId}')"]`);
        }

        const originalButtonText = button?.innerHTML || '';
        if (button) {
            button.disabled = true;
            button.innerHTML = `
                <svg class="animate-spin h-4 w-4 inline-block mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
            `;
        }

        try {
            const client = (window as any).canisterClient;
            if (!client) {
                console.error('CanisterClient not available');
                (window as any).showToast?.('Canister client not available', 'error');
                return;
            }
            console.log('📡 Calling getAllDevices...');
            const devicesResult = await client.getAllDevices();
            console.log('📦 Devices result:', devicesResult);

            // Handle both formats: raw array (mainnet) or {ok: [...]} (proxy)
            let rawDevices: any[];
            if (Array.isArray(devicesResult)) {
                rawDevices = devicesResult;
            } else if (devicesResult?.ok) {
                rawDevices = devicesResult.ok;
            } else if (devicesResult?.err) {
                console.error('Failed to load devices:', devicesResult.err);
                if (toastId) (window as any).hideToast?.(toastId);
                (window as any).showToast?.('Failed to load device data', 'error');
                if (button) {
                    button.disabled = false;
                    button.innerHTML = originalButtonText;
                }
                return;
            } else {
                rawDevices = [];
            }

            console.log('🔎 Looking for device:', deviceId, 'in', rawDevices.length, 'devices');
            console.log('🔎 First device tuple:', rawDevices[0]);

            const deviceTuple = rawDevices.find((tuple: any) => tuple[0] === deviceId);
            console.log('🔎 Found device tuple:', deviceTuple);

            if (!deviceTuple) {
                console.error('Device not found:', deviceId);
                if (toastId) (window as any).hideToast?.(toastId);
                (window as any).showToast?.('Device not found', 'error');
                if (button) {
                    button.disabled = false;
                    button.innerHTML = originalButtonText;
                }
                return;
            }

            const [id, metadataArray] = deviceTuple;
            // Convert metadata array of tuples to object
            // Format: [["key1", "value1"], ["key2", "value2"], ...]
            const metadata: Record<string, string> = {};
            if (Array.isArray(metadataArray)) {
                metadataArray.forEach((kvPair: any) => {
                    if (Array.isArray(kvPair) && kvPair.length >= 2) {
                        metadata[kvPair[0]] = kvPair[1];
                    }
                });
            }
            console.log('📋 Device metadata:', metadata);

            // Populate device detail elements
            const setElementText = (elemId: string, text: string) => {
                const elem = document.getElementById(elemId);
                if (elem) elem.textContent = text;
            };

            setElementText('device-detail-id', id);
            setElementText('device-detail-ip', metadata.ip || '-');
            setElementText('device-detail-hostname', metadata.hostname || '-');
            setElementText('device-detail-type', metadata.device_type || '-');
            setElementText('device-detail-os', metadata.os || '-');

            const statusElem = document.getElementById('device-detail-status');
            if (statusElem) {
                statusElem.textContent = metadata.status || '-';
                statusElem.className = 'text-base';
                if (metadata.status === 'online' || metadata.status === 'Online') {
                    statusElem.classList.add('text-green-600', 'font-semibold');
                } else if (metadata.status === 'offline' || metadata.status === 'Offline') {
                    statusElem.classList.add('text-red-600', 'font-semibold');
                }
            }

            // Load config files
            const configList = document.getElementById('device-config-list');
            if (configList && metadata.ip) {
                configList.innerHTML = '<p class="text-sm text-gray-500">Loading configuration files...</p>';
                try {
                    const configs = await client.getConfigsByIp(metadata.ip);
                    console.log('📁 Config files result:', configs);
                    if (!configs || configs.length === 0) {
                        configList.innerHTML = '<p class="text-sm text-gray-500">No configuration files found.</p>';
                    } else {
                        configList.innerHTML = configs.map((config: any) => {
                            // Handle different config data structures
                            const configPath = config.path || config.config_path || config[0] || 'Unknown path';
                            const configType = config.config_type || config.type || config[1] || 'Unknown type';
                            console.log('📄 Config:', config);
                            return `
                                <div class="border rounded p-2 mb-2">
                                    <p class="font-medium text-sm">${configPath}</p>
                                    <p class="text-xs text-gray-500">${configType}</p>
                                </div>
                            `;
                        }).join('');
                    }
                } catch (e) {
                    console.error('Failed to load configs:', e);
                    configList.innerHTML = '<p class="text-sm text-gray-500">No configuration files found.</p>';
                }
            }

            // Sprint 29: Reset tabs and store current device ID for activity log
            // Sprint 30: UUID is now the canonical identifier (hostname fallback removed)
            (window as any).currentDeviceId = id;
            switchDeviceTabInternal('general');

            // Show modal
            const modal = document.getElementById('device-details-modal');
            console.log('🖼️ Modal element:', modal);
            if (modal) {
                modal.classList.remove('hidden');
                console.log('✅ Modal shown');

                // Sprint 29: Log access event (non-blocking)
                logDeviceAccessEvent(id).catch(err => console.warn('Failed to log access:', err));

                // Sprint 29: Pre-load activity log count for badge
                loadActivityLogCount(id).catch(err => console.warn('Failed to load activity count:', err));
            } else {
                console.error('❌ Modal element not found: device-details-modal');
            }

            if (toastId) (window as any).hideToast?.(toastId);

        } catch (error) {
            console.error('Error viewing device:', error);
            (window as any).showToast?.('Error loading device details', 'error');
        } finally {
            if (button) {
                button.disabled = false;
                button.innerHTML = originalButtonText;
            }
        }
    }
}

// Expose viewDevice globally for onclick handlers
(window as any).viewDevice = async function(deviceId: string, buttonElement?: HTMLElement): Promise<void> {
    const pageManager = (window as any).pageManagement;
    if (pageManager) {
        await pageManager.viewDevice(deviceId, buttonElement);
    } else {
        console.error('PageManager not initialized');
    }
};

// Expose hideDeviceDetailsModal globally for modal close buttons
(window as any).hideDeviceDetailsModal = function(): void {
    const modal = document.getElementById('device-details-modal');
    if (modal) {
        modal.classList.add('hidden');
    }
    // Clear current device ID
    (window as any).currentDeviceId = null;
};

/**
 * Show device details modal from service map
 * Called when user clicks "View Full Details" button on a service map node
 * Uses IP address to look up device in vector_db (more reliable than device_id matching)
 * @param deviceInfo - Device info from service map containing device_id, ip_address, hostname, role, tier
 */
(window as any).showDeviceDetailsModal = async function(deviceInfo: {
    device_id: string;
    ip_address?: string;
    hostname?: string;
    role?: string;
    tier?: string;
    config_files?: string[];
}): Promise<void> {
    console.log('📊 showDeviceDetailsModal called with:', deviceInfo);

    if (!deviceInfo) {
        console.error('showDeviceDetailsModal: Invalid device info');
        (window as any).showToast?.('Cannot show device details: missing device info', 'error');
        return;
    }

    const canisterClient = (window as any).canisterClient;
    if (!canisterClient) {
        console.error('showDeviceDetailsModal: CanisterClient not available');
        (window as any).showToast?.('Error: Service not available', 'error');
        return;
    }

    // Try to find device by IP address first (more reliable than device_id)
    const ip = deviceInfo.ip_address;
    if (ip) {
        try {
            console.log(`🔍 Looking up device by IP: ${ip}`);
            const result = await canisterClient.getDeviceByIp(ip);
            console.log('📊 getDeviceByIp result:', result);

            // Handle Candid opt type: [] = None, [value] = Some(value)
            const device = Array.isArray(result) ? result[0] : result;

            if (device && device.sys_id) {
                console.log(`✅ Found device with sys_id: ${device.sys_id}`);
                // Use the existing viewDevice function with the found sys_id
                const viewDevice = (window as any).viewDevice;
                if (viewDevice) {
                    await viewDevice(device.sys_id);
                    return;
                }
            } else {
                console.log(`⚠️ Device not found in inventory for IP: ${ip}`);
                (window as any).showToast?.(`Device ${ip} not found in inventory. Run discovery first.`, 'warning');
            }
        } catch (error) {
            console.error('Error looking up device by IP:', error);
        }
    }

    // Fallback: try with device_id directly
    if (deviceInfo.device_id) {
        const viewDevice = (window as any).viewDevice;
        if (viewDevice) {
            try {
                await viewDevice(deviceInfo.device_id);
            } catch (err) {
                console.error('viewDevice fallback failed:', err);
                (window as any).showToast?.('Device details not available', 'warning');
            }
        }
    }
};

// ============================================================================
// Sprint 30: Device Activity Log Manager (Refactored to OOP)
// ============================================================================

/**
 * Lazy-initialized singleton for ActivityLogManager
 * Created on first use to ensure canisterClient is available
 */
let activityLogManager: ActivityLogManager | null = null;

function getActivityLogManager(): ActivityLogManager {
    if (!activityLogManager) {
        const client = (window as any).canisterClient;
        activityLogManager = createActivityLogManager(client);
    }
    return activityLogManager;
}

/**
 * Internal function to switch device detail tabs
 */
function switchDeviceTabInternal(tabName: string): void {
    // Update tab buttons
    document.querySelectorAll('.device-tab').forEach(tab => {
        tab.classList.remove('active', 'border-blue-500', 'text-blue-600');
        tab.classList.add('border-transparent', 'text-gray-500');
    });

    const activeTab = document.getElementById(`tab-${tabName}`);
    if (activeTab) {
        activeTab.classList.add('active', 'border-blue-500', 'text-blue-600');
        activeTab.classList.remove('border-transparent', 'text-gray-500');
    }

    // Update panels
    document.querySelectorAll('.device-tab-panel').forEach(panel => {
        panel.classList.add('hidden');
    });

    const activePanel = document.getElementById(`panel-${tabName}`);
    if (activePanel) {
        activePanel.classList.remove('hidden');
    }

    // Load activity logs when activity tab is selected
    if (tabName === 'activity') {
        const deviceId = (window as any).currentDeviceId;
        if (deviceId) {
            getActivityLogManager().loadLogs(deviceId).catch(err =>
                console.warn('Failed to load activity logs:', err)
            );
        }
    }
}

// Expose switchDeviceTab globally
(window as any).switchDeviceTab = switchDeviceTabInternal;

/**
 * Window function: Load next/previous page of activity logs
 * Delegates to ActivityLogManager
 */
(window as any).loadActivityPage = function(direction: 'prev' | 'next'): void {
    const manager = getActivityLogManager();
    if (direction === 'prev') {
        manager.prevPage().catch(err => console.warn('Failed to load prev page:', err));
    } else {
        manager.nextPage().catch(err => console.warn('Failed to load next page:', err));
    }
};

/**
 * Window function: Refresh activity logs
 * Delegates to ActivityLogManager
 */
(window as any).refreshActivityLogs = function(): void {
    getActivityLogManager().refresh().catch(err =>
        console.warn('Failed to refresh activity logs:', err)
    );
};

/**
 * Window function: Filter activity logs by type
 * Delegates to ActivityLogManager
 */
(window as any).filterActivityLogs = function(): void {
    const filter = (document.getElementById('activity-filter') as HTMLSelectElement)?.value || 'all';
    getActivityLogManager().setFilter(filter as any);
};

/**
 * Log device access event - Delegates to ActivityLogManager
 */
async function logDeviceAccessEvent(deviceId: string): Promise<void> {
    await getActivityLogManager().logAccess(deviceId);
}

/**
 * Load activity log count for badge - Delegates to ActivityLogManager
 */
async function loadActivityLogCount(deviceId: string): Promise<void> {
    await getActivityLogManager().loadCount(deviceId);
}

// Expose viewApplication globally for onclick handlers
(window as any).viewApplication = async function(applicationId: string, buttonElement?: HTMLElement): Promise<void> {
    console.log('🔍 Viewing application:', applicationId);

    const toastId = (window as any).showToast?.('Loading application details...', 'info');

    let button: HTMLButtonElement | null = buttonElement as HTMLButtonElement || null;
    if (!button) {
        button = document.querySelector(`button[onclick*="viewApplication('${applicationId}')"]`);
    }

    const originalButtonText = button?.innerHTML || '';
    if (button) {
        button.disabled = true;
        button.innerHTML = `
            <svg class="animate-spin h-4 w-4 inline-block mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
        `;
    }

    try {
        const client = (window as any).canisterClient;
        if (!client) {
            console.error('CanisterClient not available');
            (window as any).showToast?.('Canister client not available', 'error');
            return;
        }

        // Get all applications
        const appsResult = await client.callCanister('vector_db', 'get_all_applications', []);
        console.log('📦 Applications result:', appsResult);

        if (!appsResult || !Array.isArray(appsResult)) {
            console.error('Failed to load applications');
            if (toastId) (window as any).hideToast?.(toastId);
            (window as any).showToast?.('Failed to load application data', 'error');
            if (button) {
                button.disabled = false;
                button.innerHTML = originalButtonText;
            }
            return;
        }

        // Find the specific application
        const app = appsResult.find(([id, _]: [string, Record<string, string>]) => id === applicationId);
        console.log('🔎 Found application:', app);

        if (!app) {
            console.error('Application not found:', applicationId);
            if (toastId) (window as any).hideToast?.(toastId);
            (window as any).showToast?.('Application not found', 'error');
            if (button) {
                button.disabled = false;
                button.innerHTML = originalButtonText;
            }
            return;
        }

        const [id, metadata] = app;
        console.log('📋 Application metadata:', metadata);

        // Populate application detail elements
        const setElementText = (elemId: string, text: string) => {
            const elem = document.getElementById(elemId);
            if (elem) elem.textContent = text;
        };

        setElementText('app-detail-id', id);
        setElementText('app-detail-name', metadata.name || id);
        setElementText('app-detail-type', metadata.app_type || metadata.application_type || '-');
        setElementText('app-detail-hostname', metadata.hostname || metadata.device_id || '-');
        setElementText('app-detail-ip', metadata.ip_address || '-');
        setElementText('app-detail-role', metadata.role || '-');
        setElementText('app-detail-service', metadata.service_name || '-');
        setElementText('app-detail-tier', metadata.tier_level || '-');

        const statusElem = document.getElementById('app-detail-status');
        if (statusElem) {
            statusElem.textContent = metadata.status || '-';
            statusElem.className = 'text-base';
            if (metadata.status === 'Running') {
                statusElem.classList.add('text-green-600', 'font-semibold');
            } else if (metadata.status === 'Stopped') {
                statusElem.classList.add('text-red-600', 'font-semibold');
            } else if (metadata.status === 'Warning') {
                statusElem.classList.add('text-yellow-600', 'font-semibold');
            }
        }

        // Load relationships for this application
        const relationshipsList = document.getElementById('app-relationships-list');
        if (relationshipsList) {
            relationshipsList.innerHTML = '<p class="text-sm text-gray-500">Loading relationships...</p>';
            try {
                const relationships = await client.callCanister('vector_db', 'get_all_relationships', []);
                console.log('🔗 Relationships result:', relationships);

                // Filter relationships that involve this application
                const appRelationships = relationships.filter((rel: any) =>
                    rel.application_id === applicationId || rel.device_id === metadata.device_id
                );

                if (!appRelationships || appRelationships.length === 0) {
                    relationshipsList.innerHTML = '<p class="text-sm text-gray-500">No relationships found.</p>';
                } else {
                    relationshipsList.innerHTML = appRelationships.map((rel: any) => `
                        <div class="border rounded p-3 mb-2">
                            <div class="flex items-center gap-2">
                                <span class="font-medium">${rel.device_id || 'Unknown'}</span>
                                <span class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">${rel.relationship_type || 'relates_to'}</span>
                                <span class="font-medium">${rel.application_id || 'Unknown'}</span>
                            </div>
                            <p class="text-xs text-gray-500 mt-1">
                                ${rel.metadata?.source_tier || ''} → ${rel.metadata?.target_tier || ''}
                            </p>
                        </div>
                    `).join('');
                }
            } catch (e) {
                console.error('Failed to load relationships:', e);
                relationshipsList.innerHTML = '<p class="text-sm text-gray-500">Failed to load relationships.</p>';
            }
        }

        // Show modal
        const modal = document.getElementById('application-details-modal');
        console.log('🖼️ Modal element:', modal);
        if (modal) {
            modal.classList.remove('hidden');
            console.log('✅ Modal shown');
        } else {
            console.error('❌ Modal element not found: application-details-modal');
        }

        if (toastId) (window as any).hideToast?.(toastId);

    } catch (error) {
        console.error('Error viewing application:', error);
        (window as any).showToast?.('Error loading application details', 'error');
    } finally {
        if (button) {
            button.disabled = false;
            button.innerHTML = originalButtonText;
        }
    }
};

// Expose hideApplicationDetailsModal globally for modal close buttons
(window as any).hideApplicationDetailsModal = function(): void {
    const modal = document.getElementById('application-details-modal');
    if (modal) {
        modal.classList.add('hidden');
    }
};

/**
 * Parse metadata array to object
 * Candid returns metadata as vec record { text; text } which becomes [[key, value], ...]
 */
function parseMetadataArray(metadata: any): Record<string, string> {
    if (!metadata) return {};

    // If it's already an object
    if (!Array.isArray(metadata)) {
        return metadata as Record<string, string>;
    }

    const result: Record<string, string> = {};
    for (const item of metadata) {
        if (Array.isArray(item) && item.length >= 2) {
            result[item[0]] = item[1];
        }
    }
    return result;
}

// Expose viewRelationship globally for onclick handlers
(window as any).viewRelationship = async function(relationshipId: string, buttonElement?: HTMLElement): Promise<void> {
    console.log('🔍 Viewing relationship:', relationshipId);

    const toastId = (window as any).showToast?.('Loading relationship details...', 'info');

    let button: HTMLButtonElement | null = buttonElement as HTMLButtonElement || null;
    if (!button) {
        button = document.querySelector(`button[onclick*="viewRelationship('${relationshipId}')"]`);
    }

    const originalButtonText = button?.innerHTML || '';
    if (button) {
        button.disabled = true;
        button.innerHTML = `
            <svg class="animate-spin h-4 w-4 inline-block mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
        `;
    }

    try {
        const client = (window as any).canisterClient;
        if (!client) {
            console.error('CanisterClient not available');
            (window as any).showToast?.('Canister client not available', 'error');
            return;
        }

        // Fetch relationships and applications in parallel
        const [relationships, applications] = await Promise.all([
            client.callCanister('vector_db', 'get_all_relationships', []),
            client.callCanister('vector_db', 'get_all_applications', [])
        ]);

        console.log('📦 Relationships result:', relationships);
        console.log('📦 Applications result:', applications);

        if (!relationships || !Array.isArray(relationships)) {
            console.error('Failed to load relationships');
            if (toastId) (window as any).hideToast?.(toastId);
            (window as any).showToast?.('Failed to load relationship data', 'error');
            if (button) {
                button.disabled = false;
                button.innerHTML = originalButtonText;
            }
            return;
        }

        // Build applications map for enrichment
        const appsMap = new Map<string, Record<string, string>>();
        if (applications && Array.isArray(applications)) {
            for (const [appId, metadataArray] of applications) {
                appsMap.set(appId, parseMetadataArray(metadataArray));
            }
        }

        const rel = relationships.find((r: any) => r.relationship_id === relationshipId);
        console.log('🔎 Found relationship:', rel);

        if (!rel) {
            console.error('Relationship not found:', relationshipId);
            if (toastId) (window as any).hideToast?.(toastId);
            (window as any).showToast?.('Relationship not found', 'error');
            if (button) {
                button.disabled = false;
                button.innerHTML = originalButtonText;
            }
            return;
        }

        // Parse metadata array to object
        const metadata = parseMetadataArray(rel.metadata);
        console.log('📋 Parsed metadata:', metadata);

        // Get application metadata for enrichment
        const appMetadata = appsMap.get(rel.application_id) || {};
        console.log('📋 Application metadata:', appMetadata);

        const setElementText = (elemId: string, text: string) => {
            const elem = document.getElementById(elemId);
            if (elem) elem.textContent = text;
        };

        // Extract values from metadata and app data
        // Note: app_type is the correct field name from service_map_ops.rs
        const appName = appMetadata.name || appMetadata.application_name || rel.application_id || '-';
        const appType = appMetadata.app_type || appMetadata.application_type || appMetadata.type || '-';
        const appStatus = appMetadata.status || 'unknown';
        const appHostname = appMetadata.hostname || appMetadata.host || appMetadata.ip_address || metadata.target_hostname || '-';
        const serviceName = metadata.service_name || appMetadata.service_name || '-';
        const sourceTier = metadata.source_tier || '-';
        const targetTier = metadata.target_tier || '-';

        const createdDate = rel.created_at ?
            new Date(Number(rel.created_at) / 1000000).toLocaleString() : '-';

        // Summary Section (matches table columns)
        setElementText('rel-detail-device', rel.device_id || '-');
        setElementText('rel-detail-source-tier', sourceTier);
        setElementText('rel-detail-type', rel.relationship_type || '-');
        setElementText('rel-detail-app-name', appName);
        setElementText('rel-detail-app-type', appType);
        setElementText('rel-detail-target-tier', targetTier);
        setElementText('rel-detail-service', serviceName);
        setElementText('rel-detail-app-status', appStatus);
        setElementText('rel-detail-created', createdDate);

        // Source Details Section
        setElementText('rel-detail-device-id', rel.device_id || '-');
        setElementText('rel-detail-source-host', metadata.source_hostname || rel.device_id || '-');
        setElementText('rel-detail-source-tier-level', sourceTier);

        // Target Details Section
        setElementText('rel-detail-app', rel.application_id || '-');
        setElementText('rel-detail-target-host', appHostname);
        setElementText('rel-detail-app-type-detail', appType);
        setElementText('rel-detail-target-tier-level', targetTier);

        // Technical Details Section
        setElementText('rel-detail-id', rel.relationship_id || '-');

        const modal = document.getElementById('relationship-details-modal');
        if (modal) {
            modal.classList.remove('hidden');
            console.log('✅ Modal shown');
        } else {
            console.error('❌ Modal element not found: relationship-details-modal');
        }

        if (toastId) (window as any).hideToast?.(toastId);

    } catch (error) {
        console.error('Error viewing relationship:', error);
        (window as any).showToast?.('Error loading relationship details', 'error');
    } finally {
        if (button) {
            button.disabled = false;
            button.innerHTML = originalButtonText;
        }
    }
};

// Expose hideRelationshipDetailsModal globally
(window as any).hideRelationshipDetailsModal = function(): void {
    const modal = document.getElementById('relationship-details-modal');
    if (modal) {
        modal.classList.add('hidden');
    }
};

// Expose BusinessCapabilitiesController for onclick handlers
// This is set when the page manager initializes
(window as any).initBusinessCapabilitiesController = function(controller: BusinessCapabilitiesController): void {
    (window as any).businessCapabilitiesController = controller;
};
